import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Edit, Eye, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOPManagement = ({ user }) => {
  const navigate = useNavigate();
  const [sops, setSops] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingSopId, setEditingSopId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assigned_to: ''
  });

  // Quill editor modules
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['link'],
      [{ 'color': [] }, { 'background': [] }],
      ['clean']
    ]
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent', 'link', 'color', 'background'
  ];

  useEffect(() => {
    fetchSOPs();
    fetchUsers();
  }, []);

  const fetchSOPs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/sops`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSops(response.data);
    } catch (error) {
      toast.error('Failed to fetch SOPs');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter to show assignable users based on role
      let filteredUsers = response.data;
      if (user.role === 'manager') {
        // Managers can assign to team leads and team members
        filteredUsers = response.data.filter(u => 
          u.role === 'team_member' || u.role === 'team_lead'
        );
      } else if (user.role === 'team_lead') {
        // Team leads can assign to team members
        filteredUsers = response.data.filter(u => 
          u.role === 'team_member'
        );
      } else if (user.role === 'ceo' || user.role === 'superadmin') {
        // CEO/Superadmin can assign to anyone
        filteredUsers = response.data.filter(u => 
          u.role !== 'educator'
        );
      }
      // Filter out users with empty names
      filteredUsers = filteredUsers.filter(u => u.full_name && u.full_name.trim() !== '');
      setUsers(filteredUsers);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (isEditMode && editingSopId) {
        // Update existing SOP
        await axios.put(`${API_URL}/sops/${editingSopId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('SOP updated successfully');
      } else {
        // Create new SOP
        await axios.post(`${API_URL}/sops`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('SOP created successfully');
      }
      
      setShowModal(false);
      resetForm();
      fetchSOPs();
    } catch (error) {
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} SOP`);
    }
  };

  const handleEdit = (sop) => {
    setIsEditMode(true);
    setEditingSopId(sop.id);
    setFormData({
      title: sop.title,
      description: sop.description,
      assigned_to: sop.assigned_to || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (sopId) => {
    if (!window.confirm('Are you sure you want to delete this SOP?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/sops/${sopId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('SOP deleted successfully');
      fetchSOPs();
    } catch (error) {
      toast.error('Failed to delete SOP');
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', assigned_to: '' });
    setIsEditMode(false);
    setEditingSopId(null);
  };

  const getUserName = (userId) => {
    const u = users.find(user => user.id === userId);
    return u ? u.full_name : 'Unknown';
  };

  // Check if user can create/edit/delete SOPs
  const canManageSOP = (sop = null) => {
    // Assignees cannot edit or delete - only view
    if (sop && sop.assigned_to === user.id) {
      return false;
    }
    // Team members can never create/edit/delete
    if (user.role === 'team_member') {
      return false;
    }
    // Superadmin, CEO, Manager, Team Lead can manage
    return ['superadmin', 'ceo', 'manager', 'team_lead'].includes(user.role);
  };

  // Check if user can create SOPs
  const canCreateSOP = () => {
    return ['superadmin', 'ceo', 'manager', 'team_lead'].includes(user.role);
  };

  // Strip HTML tags for preview
  const getPlainTextPreview = (html, maxLength = 500) => {
    if (!html) return '';
    // Decode HTML entities and remove tags
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const text = tempDiv.textContent || tempDiv.innerText || '';
    // Clean up extra whitespace
    const cleanText = text.replace(/\s+/g, ' ').trim();
    return cleanText.length > maxLength ? cleanText.substring(0, maxLength) + '...' : cleanText;
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="management-page" data-testid="sop-management">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => navigate('/dashboard/cness-tasks')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              color: 'var(--text-primary)',
              transition: 'all 0.2s'
            }}
          >
            <ArrowLeft size={18} />
            Back
          </button>
          <h2 style={{ margin: 0 }}>SOP Management</h2>
        </div>
        {canCreateSOP() && (
          <button className="gradient-btn" onClick={() => setShowModal(true)} data-testid="add-sop-btn">
            Add SOP
          </button>
        )}
      </div>

      <div className="content-grid">
        {sops.map((sop) => (
          <div className="item-card card" key={sop.id} data-testid={`sop-card-${sop.id}`}>
            <div className="item-header">
              <h3>{sop.title}</h3>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button 
                  onClick={() => navigate(`/dashboard/sops/${sop.id}`)}
                  title="View SOP"
                  style={{
                    padding: '8px',
                    background: 'none',
                    border: '1px solid #2196F3',
                    borderRadius: '6px',
                    color: '#2196F3',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#2196F3';
                    e.currentTarget.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'none';
                    e.currentTarget.style.color = '#2196F3';
                  }}
                  data-testid={`view-sop-${sop.id}`}
                >
                  <Eye size={16} />
                </button>
                {canManageSOP(sop) && (
                  <>
                    <button 
                      onClick={() => handleEdit(sop)}
                      title="Edit SOP"
                      style={{
                        padding: '8px',
                        background: 'none',
                        border: '1px solid #7077FE',
                        borderRadius: '6px',
                        color: '#7077FE',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#7077FE';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'none';
                        e.currentTarget.style.color = '#7077FE';
                      }}
                      data-testid={`edit-sop-${sop.id}`}
                    >
                      <Edit size={16} />
                    </button>
                    <button 
                      onClick={() => handleDelete(sop.id)}
                      title="Delete SOP"
                      style={{
                        padding: '8px',
                        background: 'none',
                        border: '1px solid #d32f2f',
                        borderRadius: '6px',
                        color: '#d32f2f',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#d32f2f';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'none';
                        e.currentTarget.style.color = '#d32f2f';
                      }}
                      data-testid={`delete-sop-${sop.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </>
                )}
              </div>
            </div>
            <p className="item-description">
              {getPlainTextPreview(sop.description, 500)}
            </p>
            {sop.description && sop.description.length > 500 && (
              <button
                onClick={() => navigate(`/dashboard/sops/${sop.id}`)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#7077FE',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  padding: '4px 0',
                  marginBottom: '8px',
                  textDecoration: 'underline'
                }}
              >
                Read More →
              </button>
            )}
            <div className="item-footer">
              <span className="assigned-to">Assigned to: {getUserName(sop.assigned_to)}</span>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="modal-overlay" data-testid="sop-modal">
          <div className="modal-content" style={{ maxWidth: '700px' }}>
            <h3>{isEditMode ? 'Edit SOP' : 'Add New SOP'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="sop-title-input"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <div style={{ 
                  border: '1px solid var(--border-color)', 
                  borderRadius: '8px', 
                  overflow: 'hidden',
                  backgroundColor: 'white'
                }}>
                  <ReactQuill
                    theme="snow"
                    value={formData.description}
                    onChange={(value) => setFormData({ ...formData, description: value })}
                    modules={quillModules}
                    formats={quillFormats}
                    style={{ minHeight: '200px' }}
                    placeholder="Enter SOP description..."
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Assign To</label>
                <select
                  value={formData.assigned_to}
                  onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                  required
                  data-testid="sop-assignee-select"
                >
                  <option value="">Select user...</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>{u.full_name}</option>
                  ))}
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => { setShowModal(false); resetForm(); }} data-testid="cancel-sop-btn">Cancel</button>
                <button type="submit" className="gradient-btn" data-testid="submit-sop-btn">
                  {isEditMode ? 'Update SOP' : 'Add SOP'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SOPManagement;
